<!-- eslint-disable max-len -->
<!-- eslint-disable vuejs-accessibility/no-redundant-roles -->
<template>
  <div v-if="dataLoaded">
    <!-- SIDEBAR (mobile drawer) — hidden in arrival state so the new seller
         has one job: connect an account. Comes back automatically as soon
         as any plugin is connected. -->
    <TransitionRoot
      v-if="!hideSidebar"
      as="template"
      :show="sidebarOpen"
    >
      <Dialog
        class="relative z-50 lg:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    class="-m-2.5 p-2.5"
                    @click="sidebarOpen = false"
                  >
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon
                      class="size-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </TransitionChild>
              <!-- Sidebar component, swap this element with another sidebar if you like -->
              <div class="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4 bg-white">
                <div class="flex h-16 shrink-0 items-center">
                  <img
                    class="h-8 w-auto"
                    src="@/assets/logo2.png"
                    alt="LiveLink LLM"
                  >
                </div>
                <nav class="flex flex-1 flex-col">
                  <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                  >
                    <li>
                      <ul
                        role="list"
                        class="-mx-2 space-y-1"
                      >
                        <li
                          v-for="item in navigation"
                          :key="item.name"
                        >
                          <a
                            :href="item.href"
                            :class="[item.current ? 'bg-gray-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']"
                          >
                            <component
                              :is="item.icon"
                              :class="[item.current ? 'text-teal-600' : 'text-gray-400 group-hover:text-teal-600', 'size-6 shrink-0']"
                              aria-hidden="true"
                            />
                            {{ item.name }}
                          </a>
                        </li>
                      </ul>
                    </li>
                    <li>
                      <div class="text-xs/6 font-semibold text-gray-400">
                        {{ t('sidemenu.products.title') }}
                      </div>
                      <ul
                        role="list"
                        class="-mx-2 mt-2 space-y-1"
                      >
                        <li
                          v-for="product in otherProducts"
                          :key="product.name"
                        >
                          <a
                            v-if="!product.disabled"
                            :href="product.href"
                            :class="[product.current ? 'bg-gray-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']"
                          >
                            <span :class="[product.current ? 'border-teal-600 text-teal-600' : 'border-gray-200 text-gray-400 group-hover:border-teal-600 group-hover:text-teal-600', 'flex size-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium']">{{ product.initial }}</span>
                            <span class="truncate">{{ product.name }}</span>
                          </a>
                          <p
                            v-else
                            class="'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'"
                          >
                            <span class="border-gray-200 text-gray-400  flex size-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium">{{ product.initial }}</span>
                            <span class="truncate text-gray-400">{{ product.name }}</span>
                          </p>
                        </li>
                      </ul>
                    </li>
                    <li class="mt-auto">
                      <a
                        :href="router.resolve({ name: 'UserProfileManagerPage' }).href"
                        class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                      >
                        <Cog6ToothIcon
                          class="size-6 shrink-0 text-gray-400 group-hover:text-teal-600"
                          aria-hidden="true"
                        />
                        {{ t('sidemenu.navigation.settings.title') }}
                      </a>
                    </li>
                  </ul>
                  <SimpleFooter
                    :horizontal="false"
                    :no-backgound="true"
                  />
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop — hidden in arrival state (see comment above) -->
    <div
      v-if="!hideSidebar"
      class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col"
    >
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
        <div class="flex h-16 shrink-0 items-center">
          <img
            class="h-8 w-auto"
            src="@/assets/logo2.png"
            alt="Your Company"
          >
        </div>
        <nav class="flex flex-1 flex-col">
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li>
              <ul
                role="list"
                class="-mx-2 space-y-1"
              >
                <li
                  v-for="item in navigation"
                  :key="item.name"
                >
                  <a
                    :href="item.href"
                    :class="[item.current ? 'bg-gray-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']"
                  >
                    <component
                      :is="item.icon"
                      :class="[item.current ? 'text-teal-600' : 'text-gray-400 group-hover:text-teal-600', 'size-6 shrink-0']"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <div class="text-xs/6 font-semibold text-gray-400">
                {{ t('sidemenu.navigation.grow-with-us.title') }}
              </div>
              <ul
                role="list"
                class="-mx-2 mt-2 space-y-1"
              >
                <li
                  v-for="product in otherProducts"
                  :key="product.name"
                >
                  <a
                    v-if="!product.disabled"
                    :href="product.href"
                    :class="[product.current ? 'bg-gray-50 text-teal-600' : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']"
                  >
                    <span :class="[product.current ? 'border-teal-600 text-teal-600' : 'border-gray-200 text-gray-400 group-hover:border-teal-600 group-hover:text-teal-600', 'flex size-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium']">{{ product.initial }}</span>
                    <span class="truncate">{{ product.name }}</span>
                  </a>
                  <p
                    v-else
                    class="'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold'"
                  >
                    <span class="border-gray-200 text-gray-400  flex size-6 shrink-0 items-center justify-center rounded-lg border text-[0.625rem] font-medium">{{ product.initial }}</span>
                    <span class="truncate text-gray-400">{{ product.name }}</span>
                  </p>
                </li>
              </ul>
            </li>
            <li class="mt-auto">
              <a
                :href="router.resolve({ name: 'UserProfileManagerPage' }).href"
                class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-teal-600"
              >
                <Cog6ToothIcon
                  class="size-6 shrink-0 text-gray-400 group-hover:text-teal-600"
                  aria-hidden="true"
                />
                {{ t('sidemenu.navigation.settings.title') }}
              </a>
            </li>
          </ul>
          <SimpleFooter
            :horizontal="false"
            :no-backgound="true"
            :hide-label="true"
          />
        </nav>
      </div>
    </div>

    <!-- Main — content shifts right to make room for the sidebar (lg:pl-72)
         unless we're hiding the sidebar for arrival, in which case the
         content is centered edge-to-edge inside its max-w-7xl. -->
    <div :class="hideSidebar ? '' : 'lg:pl-72'">
      <!-- Sub Header -->
      <div class="bg-gray-100 sticky top-0 z-40 lg:mx-auto lg:max-w-7xl lg:px-8">
        <div class="flex h-16 items-center gap-x-4 border-b border-gray-200 px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
          <button
            v-if="!hideSidebar"
            type="button"
            class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            @click="sidebarOpen = true"
          >
            <span class="sr-only">Open sidebar</span>
            <Bars3Icon
              class="size-6"
              aria-hidden="true"
            />
          </button>

          <!-- Separator -->
          <div
            v-if="!hideSidebar"
            class="h-6 w-px bg-gray-200 lg:hidden"
            aria-hidden="true"
          />

          <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 bg-gray-100">
            <!-- <form
              class="grid flex-1 grid-cols-1"
              action="#"
              method="GET"
            >
              <input
                type="search"
                name="search"
                aria-label="Search"
                class="col-start-1 row-start-1 block size-full bg-white pl-8 text-base text-gray-900 outline-hidden placeholder:text-gray-400 sm:text-sm/6"
                placeholder="Search"
              >
              <MagnifyingGlassIcon
                class="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
                aria-hidden="true"
              />
            </form> -->
            <div class="grid flex-1 grid-cols-1" />
            <div class="flex items-center gap-x-4 lg:gap-x-6">
              <!-- <button
                type="button"
                class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
              >
                <span class="sr-only">View notifications</span>
                <BellIcon
                  class="size-6"
                  aria-hidden="true"
                />
              </button> -->

              <!-- Separator -->
              <div
                class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
                aria-hidden="true"
              />

              <!-- Profile dropdown -->
              <Menu
                as="div"
                class="relative"
              >
                <MenuButton class="-m-1.5 flex items-center p-1.5">
                  <span class="sr-only">Open user menu</span>
                  <img
                    class="size-8 rounded-full bg-gray-50"
                    :src="getProfileImageUrl()"
                    alt=""
                  >
                  <span class="hidden lg:flex lg:items-center">
                    <span
                      class="ml-4 text-sm/6 font-semibold text-gray-900"
                      aria-hidden="true"
                    >
                      {{ getUserFirstName }}
                    </span>
                    <ChevronDownIcon
                      class="ml-2 size-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </MenuButton>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems class="bg-white absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md py-2 ring-1 shadow-lg ring-gray-900/5 focus:outline-hidden">
                    <MenuItem
                      v-for="item in userNavigation"
                      :key="item.name"
                      v-slot="{ active }"
                    >
                      <a
                        :href="item.href"
                        :class="[active ? 'bg-gray-50 outline-hidden' : '', 'block px-3 py-1 text-sm/6 text-gray-900']"
                      >{{ item.name }}</a>
                    </MenuItem>
                    <div class="my-1 border-t border-gray-100" />
                    <MenuItem
                      v-slot="{ active }"
                    >
                      <a
                        href="#"
                        :class="[active ? 'bg-gray-50 outline-hidden' : '', 'block px-3 py-1 text-sm/6 text-gray-900']"
                        @click="logout"
                      >{{ t('sidemenu.navigation.signout.title') }}</a>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>

      <main class="py-4">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="border-b border-gray-200 pb-5">
            <h3 class="font-semibold text-gray-900 text-xl">
              {{ t('dashboard.title', {name: getUserFirstName}) }}
            </h3>
          </div>

          <div
            v-if="livvia.isVisible"
            class="mt-6"
          >
            <LivviaStrip />
          </div>

          <div v-if="livvia.state === 'arrival'">
            <div class="mt-6">
              <ConnectEmpty />
            </div>
          </div>
          <div v-else-if="livvia.state === 'prepare' || livvia.state === 'prepare_success'">
            <div class="mt-6">
              <PrepareCards />
            </div>
          </div>
          <div v-else-if="livvia.state === 'post_live' || livvia.state === 'post_live_deferred'">
            <div class="mt-6">
              <LivesList />
            </div>
          </div>
          <div v-else-if="livvia.state === 'video_picker' || livvia.state === 'processing'">
            <div class="mt-6">
              <VideoPicker />
            </div>
          </div>
          <div v-else-if="livvia.state === 'result'">
            <div class="mt-6">
              <LiveResult />
            </div>
          </div>
          <div v-else>
            <div class="mt-8">
              <PluginMetadatasMetrics />
            </div>
            <div class="mt-8">
              <ListPluginReportsDashboard
                :plugins="plugins"
              />
            </div>
          </div>
        </div>
      </main>

      <!-- OAuth entry point for Livvia's connect actions -->
      <PluginConnectDrawer
        v-model:show="livvia.showConnectDrawer"
        :platform="livvia.connectPlatform || undefined"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref, computed, onBeforeMount, onBeforeUnmount,
} from 'vue';
import { useRouter } from 'vue-router';
import useAuthStore from '@/store/auth';
import SimpleFooter from '@/components/tailwind/footer/SimpleFooter.vue';
import ListPluginReportsDashboard from '@/components/tailwind/plugin/ListPluginReportsDashboard.vue';
import PluginMetadatasMetrics from '@/components/tailwind/plugin/PluginMetadatasMetrics.vue';
import LivviaStrip from '@/components/tailwind/livvia/LivviaStrip.vue';
import ConnectEmpty from '@/components/tailwind/livvia/ConnectEmpty.vue';
import PrepareCards from '@/components/tailwind/livvia/PrepareCards.vue';
import LivesList from '@/components/tailwind/livvia/LivesList.vue';
import VideoPicker from '@/components/tailwind/livvia/VideoPicker.vue';
import LiveResult from '@/components/tailwind/livvia/LiveResult.vue';
import PluginConnectDrawer from '@/components/tailwind/plugin/PluginConnectDrawer.vue';
import useLivviaStore from '@/store/livvia';
import BlankProfilePicture from '@/assets/images/backgrounds/blank-profile-picture.png';
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  Bars3Icon,
  HeartIcon,
  PresentationChartBarIcon,
  Cog6ToothIcon,
  PuzzlePieceIcon,
  HomeIcon,
  XMarkIcon,
  CursorArrowRaysIcon,
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useI18n } from 'vue-i18n';
import usePluginStore from '@/store/plugin/plugin';
import Plugin from '@/models/plugin/plugin';

const router = useRouter();
const { session, authDoLogout } = useAuthStore();
const { t } = useI18n();
const {
  pluginsDoGetMy,
  pluginAnalyzeTranscription,
  pluginGetTranscriptionAnalysisStatus,
  pluginGetTranscriptionAnalysis,
  pluginSetLiveCartStatus,
  pluginUpdateSettings,
} = usePluginStore();
const livvia = useLivviaStore();

// Bind the plugin-store wrappers to Livvia's pipeline hook so the store
// stays framework-clean (no cross-store imports). Small adapters shim
// between the branch's method signatures and the PipelineAPI shape.
livvia.registerPipelineAPI({
  analyze: async ({ mediaId }) => {
    const r = await pluginAnalyzeTranscription(mediaId);
    return { status: (r as any)?.status || 'started' };
  },
  getStatus: async (mediaId) => {
    const r = await pluginGetTranscriptionAnalysisStatus(mediaId);
    return (r as any) || {};
  },
  getResult: pluginGetTranscriptionAnalysis,
  // TODO: wire aggregate to pluginGetAILiveShoppingCarts once we know the
  // accountId → InstagramLiveAIMetadataModel → CartLine mapping. Until
  // then, returning empty falls back to Livvia's demo mock so the flow
  // still walks end-to-end.
  aggregate: async () => ({ total_amount: 0, client_count: 0, carts: [] }),
  setCartStatus: async ({
    pluginId, mediaId, buyerUserId, status, reason,
  }) => pluginSetLiveCartStatus({
    pluginId, mediaId, buyerUserId, status, reason,
  }),
  updateSettings: async ({ pluginId, autoMessageEnabled }) => (
    pluginUpdateSettings({ pluginId, autoMessageEnabled })
  ),
});

/*
  Data
*/
const dataLoaded = ref(false);
const sidebarOpen = ref(false);
const plugins = ref([] as Plugin[]);

/*
  Methods
*/
// Preview hook — lets us walk the post-live flow in dev without a real
// pending live. Gated so it only works on local/staging builds; production
// builds ignore the param entirely.
const applyLivviaDevTrigger = (): boolean => {
  if (process.env.NODE_ENV === 'production') return false;
  const param = new URLSearchParams(window.location.search).get('livvia');
  if (param === 'post_live') {
    livvia.enterPostLive({
      durationLabel: '1h 42',
      commentsCount: 340,
      timestampLabel: t('livvia.postLive.mock.latestTitle'),
      platform: 'instagram',
    });
    return true;
  }
  return false;
};

const loadPlugins = async () => {
  await pluginsDoGetMy()
    .then((response) => {
      if (response !== undefined) {
        // Combine Instagram and Facebook plugins
        const allPlugins = [
          ...response.instagramPlugins.map((p: any) => new Plugin(p)),
          ...response.facebookPlugins.map((p: any) => new Plugin(p)),
        ].sort((a, b) => a.name.localeCompare(b.name));
        plugins.value = allPlugins;
      }
    }).catch((error) => {
      console.error('error', error);
    }).finally(() => {
      // Sellers with a single plugin have no "which one?" to make — their
      // dashboard IS that plugin's dashboard. Skip the multi-plugin overview
      // (and Livvia) and drop them straight in. Dev-trigger overrides so we
      // can still preview the multi-view + Livvia states.
      const igfbPlugins = plugins.value.filter(
        (p): p is typeof p & { type: 'instagram' | 'facebook' } => (
          p.type === 'instagram' || p.type === 'facebook'
        ),
      );
      const devTriggered = applyLivviaDevTrigger();
      // Single plugin → drop the seller straight into that plugin's
      // dashboard. Multiple plugins → send them to the manager so they
      // pick which one to work on. Zero plugins falls through to the
      // arrival/Livvia state below.
      if (!devTriggered && igfbPlugins.length === 1) {
        const only = igfbPlugins[0];
        router.replace({
          name: 'PluginDashboardPage',
          params: { pluginId: only.id, pluginType: only.type },
        });
        return;
      }
      if (!devTriggered && igfbPlugins.length > 1) {
        router.replace({ name: 'PluginsManagerPage' });
        return;
      }
      if (!devTriggered) {
        livvia.deriveFromPlugins(igfbPlugins.map((p) => ({
          id: p.id,
          type: p.type,
          pendingFullSyncMediaId: p.pendingFullSyncMediaId,
          lastSeenLive: p.lastSeenLive,
          autoMessageEnabled: p.autoMessageEnabled,
        })));
      }
      dataLoaded.value = true;
    });
};
const getProfileImageUrl = () => session?.profileImageUrl || BlankProfilePicture;
const logout = async () => {
  await authDoLogout().then(() => {
    window.open(router.resolve({ name: 'SessionLoginPage' }).href, '_self');
  }).catch((error) => {
    console.error(error);
    alert(t('session.logoutError'));
    // }).finally(() => {
    // window.location.reload();
  });
};

/*
  Computed
*/
const getUserFirstName = computed(() => session?.name.split(' ')[0]);
// Hide the whole sidebar (mobile drawer + desktop static column) while the
// seller has no plugin yet. Removes menu distractions during the very first
// integration; comes back the moment `deriveFromPlugins` sees a plugin.
const hideSidebar = computed(() => livvia.state === 'arrival');
const otherProducts = computed(() => [
  {
    id: 1, name: t('sidemenu.navigation.bio.title'), href: router.resolve({ name: 'UserBioManagerPage' }).href, initial: t('sidemenu.navigation.bio.initials'), current: false, disabled: false,
  },
  {
    id: 2, name: t('sidemenu.navigation.myBrand.title'), href: router.resolve({ name: 'MyMerchantsPage' }).href, initial: t('sidemenu.navigation.myBrand.initials'), current: false, disabled: false,
  },
]);
const userNavigation = computed(() => [
  { name: t('sidemenu.navigation.profile.title'), href: router.resolve({ name: 'UserProfileManagerPage' }).href, method: null },
  { name: t('plugins.manager.title'), href: router.resolve({ name: 'PluginsManagerPage' }).href, method: null },
  { name: t('sidemenu.navigation.bio.title'), href: router.resolve({ name: 'UserBioManagerPage' }).href, method: null },
  // { name: t('sidemenu.navigation.signout.title'), href: '#', method: logout },
]);
const navigation = computed(() => [
  {
    name: t('sidemenu.navigation.dashboard.title'), href: router.resolve({ name: 'DashboardPage' }).href, icon: HomeIcon, current: true,
  },
  {
    name: t('sidemenu.navigation.integration.title'), href: router.resolve({ name: 'PluginsManagerPage' }).href, icon: PuzzlePieceIcon, current: false,
  },
  {
    name: t('sidemenu.navigation.crm.title'), href: router.resolve({ name: 'CRMDashboardPage' }).href, icon: CursorArrowRaysIcon, current: false,
  },
  {
    name: t('sidemenu.navigation.shared-report.title'), href: router.resolve({ name: 'PluginLivesSharedWithMePage' }).href, icon: PresentationChartBarIcon, current: false,
  },
  {
    name: t('sidemenu.navigation.affiliate-panel.title'), href: router.resolve({ name: 'AffiliateDashboardPage' }).href, icon: HeartIcon, current: false,
  },
]);

/*
  Hooks
*/
onBeforeMount(() => {
  loadPlugins();
});

// Kill any in-flight processing timers if the user navigates away
// mid-flow so state doesn't mutate after the component is gone.
onBeforeUnmount(() => {
  livvia.cancelProcessing();
});
</script>
